import React, { createContext, useContext, useState, ReactNode } from "react";

interface GameStats {
  totalGames: number;
  totalWins: number;
  consecutiveWins: number;
  consecutiveLosses: number;
  gameHistory: boolean[];
}

interface GameStatsContextType {
  stats: GameStats;
  updateStats: (won: boolean, wonAllCoins?: boolean) => void;
  resetStats: () => void;
}

const GameStatsContext = createContext<GameStatsContextType | undefined>(
  undefined
);

export const useGameStats = () => {
  const context = useContext(GameStatsContext);
  if (!context) {
    throw new Error("useGameStats must be used within a GameStatsProvider");
  }
  return context;
};

interface GameStatsProviderProps {
  children: ReactNode;
}

export const GameStatsProvider: React.FC<GameStatsProviderProps> = ({
  children,
}) => {
  const [stats, setStats] = useState<GameStats>({
    totalGames: 0,
    totalWins: 0,
    consecutiveWins: 0,
    consecutiveLosses: 0,
    gameHistory: [],
  });

  const updateStats = (won: boolean, wonAllCoins = false) => {
    setStats((prev) => ({
      totalGames: prev.totalGames + 1,
      totalWins: won ? prev.totalWins + 1 : prev.totalWins,
      consecutiveWins: won ? prev.consecutiveWins + 1 : 0,
      consecutiveLosses: won ? 0 : prev.consecutiveLosses + 1,
      gameHistory: [...prev.gameHistory.slice(-9), won],
    }));
  };

  const resetStats = () => {
    setStats({
      totalGames: 0,
      totalWins: 0,
      consecutiveWins: 0,
      consecutiveLosses: 0,
      gameHistory: [],
    });
  };

  return (
    <GameStatsContext.Provider value={{ stats, updateStats, resetStats }}>
      {children}
    </GameStatsContext.Provider>
  );
};
